import React, { useState, useEffect } from 'react';
import { Select, Slider, Typography, Divider, Space, Button, Input, Spin, notification } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import api from '../api';
import { useFilters } from '../contexts/FilterContext';
import { useAuth } from '../contexts/AuthContext';

const { Text, Title } = Typography;
const { Option } = Select;

interface FilterOptions {
  drug_classes: string[];
  therapeutic_categories: string[];
  regions: string[];
  countries: string[]; // Added for country filtering
  phases: string[];
  classifications: string[];
  indication_types: string[];
  developers: string[];
  year_range: { min_year: number; max_year: number };
  // Market filters
  routes_of_administration: string[];
  distribution_channels: string[];
  drug_types: string[];
  mechanisms_of_action: string[];
  small_molecule_drug_classes: string[];
  prescription_statuses: string[];
  patent_statuses: string[];
  hierarchies?: { [dimension: string]: { [child: string]: string } };
}

interface FilterPanelProps { activeTab: string }

export const FilterPanel: React.FC<FilterPanelProps> = ({ activeTab }) => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const { 
    filters, 
    updateFilter, 
    resetFilters,
    resetTASpecificFilters,
    isClassMode,
    isCategoryMode,
    isRouteMode,
    isDistributionMode,
    isDrugTypeMode,
    isMechanismMode,
    isSmallMoleculeMode,
    isPrescriptionStatusMode,
    isPatentStatusMode,
    isUnfilteredMode,
    hierarchies: contextHierarchies,
    setHierarchies
  } = useFilters();
  const { user } = useAuth();
  const [pipelineFilterOptions, setPipelineFilterOptions] = useState<{ 
    phases: string[], 
    classifications: string[],
    indications?: string[],
    mechanisms?: string[],
    routes?: string[],
    drug_types?: string[],
    therapy_classes?: string[],
    therapeutic_approaches?: string[],
    modalities?: string[],
    delivery_technologies?: string[],
    disease_categories?: string[]
  }>({ phases: [], classifications: [] });
  const [companyOptions, setCompanyOptions] = useState<string[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const isExecutiveSummaryTab = activeTab === 'executive-summary';
  const isMarketOverviewTab = activeTab === 'market-overview';
  const isMarketBreakdownsTab = activeTab === 'market-breakdowns';
  const isRegionalAnalysisTab = activeTab === 'regional-analysis';
  const isCountryInsightsTab = activeTab === 'country-insights';
  const isDrugPipelineTab = activeTab === 'drug-pipeline';

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Refetch filter options when therapeutic area changes
  useEffect(() => {
    fetchFilterOptions();
    // Clear drug class and therapeutic category filters when therapeutic area changes
    // since the available options will be different
    if (filters.drugClasses.length > 0) {
      updateFilter('drugClasses', []);
    }
    if (filters.therapeuticCategories.length > 0) {
      updateFilter('therapeuticCategories', []);
    }
  }, [filters.therapeuticArea]);

  // Auto-set therapeutic area when user data is available
  // Year range will be automatically set by FilterContext based on therapeutic area
  useEffect(() => {
    if (user && user.effective_therapeutic_areas) {
      // If user has only one therapeutic area, set it automatically
      if (user.effective_therapeutic_areas.length === 1) {
        updateFilter('therapeuticArea', user.effective_therapeutic_areas[0]);
      }
      // If user has multiple therapeutic areas and no TA is set, default to first one
      else if (user.effective_therapeutic_areas.length > 1 && filters.therapeuticArea === 'Skin Cancer') {
        // Keep the default or let user choose
      }
    }
  }, [user]);

  useEffect(() => {
    if (isDrugPipelineTab) {
      fetchPipelineFilterOptions();
    }
  }, [isDrugPipelineTab, filters.pipelineStatus, filters.therapeuticArea]);

  // Fetch company options for tabs that need company filtering
  useEffect(() => {
    if (activeTab === 'company-performance' || activeTab === 'drug-pipeline') {
      fetchCompanyOptions();
    }
  }, [activeTab, filters.therapeuticArea, filters.pipelineStatus]);

  // Handle company selection when switching to Company Performance tab
  useEffect(() => {
    if (activeTab === 'company-performance' && filters.companySearchMulti.length > 0) {
      // Filter out companies that don't have active drugs
      const activeCompanies = filters.companySearchMulti.filter(company => 
        companyOptions.includes(company)
      );
      
      if (activeCompanies.length !== filters.companySearchMulti.length) {
        const removedCompanies = filters.companySearchMulti.filter(company => 
          !companyOptions.includes(company)
        );
        console.log('Removed companies without active drugs:', removedCompanies);
        updateFilter('companySearchMulti', activeCompanies);
        
        // Show notification to user
        notification.info({
          message: 'Companies Filtered',
          description: `${removedCompanies.length} company(ies) were removed because they don't have active drugs: ${removedCompanies.join(', ')}`,
          duration: 4,
        });
      }
    }
  }, [activeTab, filters.companySearchMulti, companyOptions]);


  const fetchPipelineFilterOptions = async () => {
    try {
      const params = new URLSearchParams({
        status: filters.pipelineStatus
      });
      
      // Add therapeutic area if specified
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }
      
      const response = await api.get(`/api/pipeline/filter-options?${params.toString()}`);
      setPipelineFilterOptions(response.data);
      console.log('PIPELINE FILTER OPTIONS UPDATED:', response.data);
      
      // Clear/update phase and classification filters when status changes to prevent invalid selections
      if (filters.phaseFilter.length > 0) {
        const validPhases = filters.phaseFilter.filter(p => response.data.phases.includes(p));
        if (validPhases.length !== filters.phaseFilter.length) {
          updateFilter('phaseFilter', validPhases);
          console.log('UPDATED INVALID PHASE FILTERS. Kept:', validPhases);
        }
      }
      if (filters.classificationFilter.length > 0) {
        const validClassifications = filters.classificationFilter.filter(c => response.data.classifications.includes(c));
        if (validClassifications.length !== filters.classificationFilter.length) {
          updateFilter('classificationFilter', validClassifications);
          console.log('UPDATED INVALID CLASSIFICATION FILTERS. Kept:', validClassifications);
        }
      }
    } catch (error) {
      console.error('ERROR FETCHING PIPELINE FILTER OPTIONS:', error);
      setPipelineFilterOptions({ phases: [], classifications: [] });
    }
  };

  const fetchCompanyOptions = async () => {
    try {
      setCompanyLoading(true);
      const params = new URLSearchParams({ limit: '500' }); // Get a large number of companies
      
      // Add therapeutic area filter if specified
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }
      
      // Add pipeline status filter
      if (activeTab === 'drug-pipeline' && filters.pipelineStatus) {
        // Drug Pipeline tab uses the selected pipeline status
        params.append('pipeline_status', filters.pipelineStatus);
      } else if (activeTab === 'company-performance') {
        // Company Performance tab always shows only active drugs
        params.append('pipeline_status', 'active');
      }
      
      const response = await api.get(`/api/companies/search?${params.toString()}`);
      const companies = response.data.companies || response.data || [];
      // Extract company names from the response
      const companyNames = companies.map((company: any) => company.name || company).filter(Boolean);
      setCompanyOptions(companyNames);
    } catch (error) {
      console.error('ERROR FETCHING COMPANY OPTIONS:', error);
      setCompanyOptions([]);
    } finally {
      setCompanyLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      console.log('🔍 Fetching filter options for therapeutic area:', filters.therapeuticArea);
      
      // Build query parameters with therapeutic area
      const params = new URLSearchParams();
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }
      
      const url = `/api/filter-options?${params.toString()}`;
      console.log('🌐 Making API call to:', url);
      
      const response = await api.get(url);
      console.log('✅ API response received:', response.data);
      
      setFilterOptions(response.data);
      if (response.data.hierarchies) {
        setHierarchies(response.data.hierarchies);
      }
      console.log('📊 Filter options updated for therapeutic area:', filters.therapeuticArea, response.data);
      
      // Specifically log drug_types for debugging
      if (response.data.drug_types) {
        console.log('💊 Drug types found:', response.data.drug_types);
      } else {
        console.log('❌ No drug_types in response');
      }
    } catch (error: any) {
      console.error('❌ Error fetching filter options:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      setFilterOptions({
        drug_classes: ['Chemotherapy', 'Immunotherapy', 'Targeted Agents'],
        therapeutic_categories: ['Melanoma', 'BCC/SCC', 'MCC'],
        regions: ['North America', 'Europe', 'Asia Pacific', 'Middle East & Africa', 'Latin & Central America'],
        countries: ['USA', 'China', 'Japan', 'Germany', 'France', 'UK', 'Italy', 'Spain', 'Canada', 'Australia', 'Brazil', 'India', 'South Korea'],
        phases: ['Phase I', 'Phase II', 'Phase III'],
        classifications: ['Small Molecule', 'Monoclonal Antibody'],
        indication_types: ['Melanoma', 'Squamous Cell'],
        developers: ['Merck', 'Pfizer', 'Roche'],
        year_range: { min_year: 2019, max_year: 2034 },
        // Market filters
        routes_of_administration: ['Topical', 'Oral', 'Parenteral'],
        distribution_channels: ['PoC/Clinic', 'Retail Pharmacies', 'Digital Pharmacies', 'D2C'],
        drug_types: ['Small Molecules', 'Biologics', 'Other'],
        mechanisms_of_action: ['PD-1/PD-L1 Inhibitors', 'CTLA-4 Inhibitors', 'BRAF Inhibitors', 'MEK Inhibitors', 'Other'],
        small_molecule_drug_classes: ['Kinase Inhibitors', 'Receptor Antagonists', 'Enzyme Inhibitors', 'Other'],
        prescription_statuses: ['Prescription (RX)', 'Over-The-Counter (OTC)'],
        patent_statuses: ['Originator', 'Generic', 'Biosimilar']
      });
    }
  };

  const handleYearRangeChange = (value: number[]) => {
    // Ensure we always have a valid array with 2 elements
    const safeValue = Array.isArray(value) && value.length === 2 
      ? value 
      : [dynamicYearRange.min_year, dynamicYearRange.max_year];
    updateFilter('yearRange', safeValue);
  };

  // Get dynamic year range based on therapeutic area
  const getYearRange = () => {
    // Dermatology domain
    if (filters.therapeuticArea === 'Dermatology - In Sight' || filters.therapeuticArea === 'Dermatology') {
      return { min_year: 2020, max_year: 2035 };
    } else if (filters.therapeuticArea === 'Dermatology Pipeline') {
      return { min_year: 2020, max_year: 2035 };
    } else if (filters.therapeuticArea === 'Psoriasis - Focus' || filters.therapeuticArea === 'Psoriasis') {
      return { min_year: 2020, max_year: 2035 };
    } else if (filters.therapeuticArea === 'Skin Cancer - Trial Version') {
      return { min_year: 2019, max_year: 2025 };
    } else if (filters.therapeuticArea === 'Skin Cancer - Focus' || filters.therapeuticArea === 'Skin Cancer') {
      return { min_year: 2019, max_year: 2034 };
    // Ophthalmology domain
    } else if (filters.therapeuticArea === 'Ophthalmology - In Sight' || filters.therapeuticArea === 'Ophthalmology') {
      return { min_year: 2021, max_year: 2036 };
    } else if (filters.therapeuticArea === 'Retinal - Focus' || filters.therapeuticArea === 'Retinal Disorders') {
      return { min_year: 2021, max_year: 2036 };
    } else if (filters.therapeuticArea === 'Glaucoma - Focus' || filters.therapeuticArea === 'Glaucoma') {
      return { min_year: 2021, max_year: 2036 };
    // Cell, Gene & RNA domain
    } else if (filters.therapeuticArea === 'Cell, Gene & RNA Therapies - In Sight' || filters.therapeuticArea === 'Cell, Gene & RNA Therapies') {
      return { min_year: 2020, max_year: 2036 };
    }
    // Fallback to filterOptions if available, otherwise use trial version range (most restrictive)
    return filterOptions?.year_range || { min_year: 2019, max_year: 2025 };
  };

  const dynamicYearRange = getYearRange();

  // Helper to render select options with hierarchy (nested or OptGroup)
  const renderOptionsWithHierarchy = (options: string[], dimensionKey: string) => {
    if (!filterOptions || !filterOptions.hierarchies || !filterOptions.hierarchies[dimensionKey]) {
      return options.map(opt => <Option key={optionKey(opt)} value={opt}>{opt}</Option>);
    }

    const hierarchy = filterOptions.hierarchies[dimensionKey];
    const topLevel = options.filter(opt => !hierarchy[opt]);
    const childrenByParent: { [parent: string]: string[] } = {};

    options.forEach(opt => {
      const parent = hierarchy[opt];
      if (parent) {
        if (!childrenByParent[parent]) childrenByParent[parent] = [];
        childrenByParent[parent].push(opt);
      }
    });

    return topLevel.map(parent => (
      <React.Fragment key={optionKey(parent)}>
        <Option value={parent} style={{ fontWeight: 'normal' }}>
          {parent}
        </Option>
        {childrenByParent[parent]?.map((child) => (
          <Option key={optionKey(child)} value={child} style={{ 
            paddingLeft: '24px',
            fontWeight: 'normal',
            fontSize: '12px',
            color: '#8c8c8c'
          }}>
            {child}
          </Option>
        ))}
      </React.Fragment>
    ));
  };

  // Helper to ensure unique keys for options
  const optionKey = (name: string) => `opt-${name.replace(/\s+/g, '-').toLowerCase()}`;

  // Helper to build reverse hierarchy (parent -> children)
  const buildReverseHierarchy = (dimensionKey: string): { [parent: string]: string[] } => {
    if (!filterOptions || !filterOptions.hierarchies || !filterOptions.hierarchies[dimensionKey]) {
      return {};
    }
    const hierarchy = filterOptions.hierarchies[dimensionKey];
    const reverse: { [parent: string]: string[] } = {};
    Object.entries(hierarchy).forEach(([child, parent]) => {
      if (!reverse[parent]) {
        reverse[parent] = [];
      }
      reverse[parent].push(child);
    });
    return reverse;
  };

  // Handler for Drug Class filter that auto-selects children when parent is selected
  const handleDrugClassChange = (value: string[]) => {
    if (!filterOptions || !filterOptions.hierarchies || !filterOptions.hierarchies['drug_classes']) {
      updateFilter('drugClasses', value);
      return;
    }

    const reverseHierarchy = buildReverseHierarchy('drug_classes');
    const previousSelection = filters.drugClasses || [];
    let newValue = [...value];

    // Find newly selected items (items in value but not in previousSelection)
    const newlySelected = value.filter(item => !previousSelection.includes(item));
    
    // For each newly selected item, if it's a parent, auto-select all its children
    newlySelected.forEach(selectedItem => {
      const children = reverseHierarchy[selectedItem];
      if (children && children.length > 0) {
        // This is a parent - add all its children if not already in the selection
        children.forEach(child => {
          if (!newValue.includes(child)) {
            newValue.push(child);
          }
        });
      }
    });

    // Find deselected items (items in previousSelection but not in value)
    const deselected = previousSelection.filter(item => !value.includes(item));
    
    // For each deselected item, if it's a parent, remove all its children
    deselected.forEach(deselectedItem => {
      const children = reverseHierarchy[deselectedItem];
      if (children && children.length > 0) {
        // This is a parent that was deselected - remove all its children
        children.forEach(child => {
          newValue = newValue.filter(item => item !== child);
        });
      }
    });

    updateFilter('drugClasses', newValue);
  };

  if (!filterOptions) {
    return (
      <div style={{ padding: '16px' }}>
        <Text>Loading filters...</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', paddingBottom: '24px' }}>
      {!isExecutiveSummaryTab && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={5} style={{ margin: 0 }}>
            Filters & Slicers
          </Title>
          <Button 
            size="small" 
            icon={<ReloadOutlined />} 
            onClick={resetFilters}
            title="Reset all filters"
          >
            Reset
          </Button>
        </div>
      )}
      {/* Helper subtitle removed per request (self-explanatory) */}

      {/* Therapeutic Area Selector - Show only if user has multiple TAs */}
      {user && user.effective_therapeutic_areas && user.effective_therapeutic_areas.length > 1 && (
        <div style={{ marginBottom: '16px' }}>
          <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
            Therapeutic Area
          </Text>
          <Select
            value={filters.therapeuticArea}
            style={{ width: '100%' }}
            size="small"
            onChange={(value) => updateFilter('therapeuticArea', value)}
            placeholder="Select therapeutic area..."
          >
            {/* Individual therapeutic areas */}
            {user.effective_therapeutic_areas.map(ta => (
              <Option key={ta} value={ta}>
                {/* Dermatology domain */}
                {ta === 'Psoriasis' ? 'Psoriasis - Focus' :
                 ta === 'Skin Cancer' ? 'Skin Cancer - Focus' :
                 ta === 'Dermatology' ? 'Dermatology - In Sight' :
                 /* Ophthalmology domain */
                 ta === 'Ophthalmology' ? 'Ophthalmology - In Sight' :
                 ta === 'Retinal Disorders' ? 'Retinal - Focus' :
                 ta === 'Glaucoma' ? 'Glaucoma - Focus' :
                 /* Cell, Gene & RNA domain */
                 ta === 'Cell, Gene & RNA Therapies' ? 'Cell, Gene & RNA Therapies - In Sight' : ta}
              </Option>
            ))}
          </Select>
        </div>
      )}

      {/* Market Overview Tab - Only Year Range */}
      {isMarketOverviewTab && (
        <>
          {/* Year Range Slider */}
          <div style={{ marginBottom: '24px', width: '100%' }}>
            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
              Year Range
            </Text>
            <div style={{ width: '100%', paddingLeft: '7px', paddingRight: '7px', boxSizing: 'border-box' }}>
              <Slider
                range
                min={dynamicYearRange.min_year}
                max={dynamicYearRange.max_year}
                value={Array.isArray(filters.yearRange) && filters.yearRange.length === 2 
                  ? filters.yearRange 
                  : [dynamicYearRange.min_year, dynamicYearRange.max_year]}
                onChange={handleYearRangeChange}
                marks={{
                  [dynamicYearRange.min_year]: dynamicYearRange.min_year.toString(),
                  [dynamicYearRange.max_year]: dynamicYearRange.max_year.toString()
                }}
                tooltip={{
                  formatter: (value) => value?.toString()
                }}
                style={{ width: '100%', margin: '0' }}
              />
            </div>
          </div>
        </>
      )}

      {/* Market Breakdowns Tab - Shows different filters based on therapeutic area */}
      {isMarketBreakdownsTab && (
        <>
          {/* Year Range Slider */}
          <div style={{ marginBottom: '24px', width: '100%' }}>
            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
              Year Range
            </Text>
            <div style={{ width: '100%', paddingLeft: '7px', paddingRight: '7px', boxSizing: 'border-box' }}>
              <Slider
                range
                min={dynamicYearRange.min_year}
                max={dynamicYearRange.max_year}
                value={Array.isArray(filters.yearRange) && filters.yearRange.length === 2 
                  ? filters.yearRange 
                  : [dynamicYearRange.min_year, dynamicYearRange.max_year]}
                onChange={handleYearRangeChange}
                marks={{
                  [dynamicYearRange.min_year]: dynamicYearRange.min_year.toString(),
                  [dynamicYearRange.max_year]: dynamicYearRange.max_year.toString()
                }}
                tooltip={{
                  formatter: (value) => value?.toString()
                }}
                style={{ width: '100%', margin: '0' }}
              />
            </div>
          </div>

          {/* Show these filters only for Dermatology */}
          {filters.therapeuticArea === 'Dermatology' && (
            <>
              {/* Indication/Therapeutic Class Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Indication/ Therapeutic Class
                </Text>
                <Select
                  mode="multiple"
                  value={filters.therapeuticCategories}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('therapeuticCategories', value)}
                  placeholder="Select indication/therapeutic class..."
                  maxTagCount="responsive"
                  disabled={isClassMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.therapeutic_categories || [], 'therapeutic_categories')}
                </Select>
                {isClassMode && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because Drug Class filter is active. Clear Drug Class to filter by Indication/ Therapeutic Class.
                  </Text>
                )}
              </div>

              {/* Route of Administration Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Route of Administration
                </Text>
                <Select
                  mode="multiple"
                  value={filters.routesOfAdministration}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('routesOfAdministration', value)}
                  placeholder="Select routes..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isCategoryMode || isDistributionMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.routes_of_administration || [], 'routes_of_administration')}
                </Select>
                {(isClassMode || isCategoryMode || isDistributionMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to enable this filter.
                  </Text>
                )}
              </div>

              {/* Drug Type Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Drug Type
                </Text>
                <Select
                  mode="multiple"
                  value={filters.drugTypes}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('drugTypes', value)}
                  placeholder="Select drug types..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isCategoryMode || isRouteMode || isDistributionMode || isMechanismMode || isSmallMoleculeMode}
                >
                  {filterOptions?.drug_types?.map((drugType: string) => (
                    <Option key={drugType} value={drugType}>{drugType}</Option>
                  )) || []}
                </Select>
                {(isClassMode || isCategoryMode || isRouteMode || isDistributionMode || isMechanismMode || isSmallMoleculeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Drug Type.
                  </Text>
                )}
              </div>

              {/* Distribution Channel Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Distribution Channel
                </Text>
                <Select
                  mode="multiple"
                  value={filters.distributionChannels}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('distributionChannels', value)}
                  placeholder="Select channels..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isCategoryMode || isRouteMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode}
                >
                  {filterOptions?.distribution_channels?.map((channel: string) => (
                    <Option key={channel} value={channel}>{channel}</Option>
                  )) || []}
                </Select>
                {(isClassMode || isCategoryMode || isRouteMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to enable this filter.
                  </Text>
                )}
              </div>

              {/* Drug Class Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Drug Class
                </Text>
                <Select
                  mode="multiple"
                  value={filters.drugClasses}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('drugClasses', value)}
                  placeholder="Select drug classes..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.drug_classes || [], 'drug_classes')}
                </Select>
                {(isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Drug Class.
                  </Text>
                )}
              </div>

              {/* Mechanism of Action Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Mechanism of Action (Biologics)
                </Text>
                <Select
                  mode="multiple"
                  value={filters.mechanismsOfAction}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('mechanismsOfAction', value)}
                  placeholder="Select mechanisms of action..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isSmallMoleculeMode}
                >
                  {filterOptions?.mechanisms_of_action?.map((mechanism: string) => (
                    <Option key={mechanism} value={mechanism}>{mechanism}</Option>
                  )) || []}
                </Select>
                {(isClassMode || isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isSmallMoleculeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Mechanism of Action.
                  </Text>
                )}
              </div>

              {/* Small Molecule Drug Class Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Drug Class (Small Molecules)
                </Text>
                <Select
                  mode="multiple"
                  value={filters.smallMoleculeDrugClasses}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('smallMoleculeDrugClasses', value)}
                  placeholder="Select small molecule drug classes..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isMechanismMode}
                >
                  {filterOptions?.small_molecule_drug_classes?.map((smallMoleculeClass: string) => (
                    <Option key={smallMoleculeClass} value={smallMoleculeClass}>{smallMoleculeClass}</Option>
                  )) || []}
                </Select>
                {(isClassMode || isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isMechanismMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Small Molecule Drug Class.
                  </Text>
                )}
              </div>
            </>
          )}

          {/* Show different secondary filters based on therapeutic area */}
          {filters.therapeuticArea === 'Psoriasis' && (
            <>
              {/* Drug Class Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Drug Class
                </Text>
                <Select
                  mode="multiple"
                  value={filters.drugClasses}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('drugClasses', value)}
                  placeholder="Select drug classes..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.drug_classes || [], 'drug_classes')}
                </Select>
                {(isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Drug Class.
                  </Text>
                )}
              </div>

              {/* Route of Administration Filter - Psoriasis only */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Route of Administration
                </Text>
                <Select
                  mode="multiple"
                  value={filters.routesOfAdministration}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('routesOfAdministration', value)}
                  placeholder="Select routes of administration..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isDistributionMode}
                >
                  {filterOptions?.routes_of_administration?.map((route: string) => (
                    <Option key={route} value={route}>{route}</Option>
                  )) || []}
                </Select>
                {(isClassMode || isDistributionMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Route of Administration.
                  </Text>
                )}
              </div>

              {/* Distribution Channel Filter - Psoriasis only */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Distribution Channel
                </Text>
                <Select
                  mode="multiple"
                  value={filters.distributionChannels}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('distributionChannels', value)}
                  placeholder="Select distribution channels..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isRouteMode}
                >
                  {filterOptions?.distribution_channels?.map((channel: string) => (
                    <Option key={channel} value={channel}>{channel}</Option>
                  )) || []}
                </Select>
                {(isClassMode || isRouteMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Distribution Channel.
                  </Text>
                )}
              </div>
            </>
          )}
          {filters.therapeuticArea === 'Skin Cancer' && (
            <>
              {/* Drug Class Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Drug Class
                </Text>
                <Select
                  mode="multiple"
                  value={filters.drugClasses}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('drugClasses', value)}
                  placeholder="Select drug classes..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.drug_classes || [], 'drug_classes')}
                </Select>
                {(isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isMechanismMode || isSmallMoleculeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Drug Class.
                  </Text>
                )}
              </div>

              {/* Therapeutic Categories Filter - Skin Cancer only */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Therapeutic Categories
                </Text>
                <Select
                  mode="multiple"
                  value={filters.therapeuticCategories}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('therapeuticCategories', value)}
                  placeholder="Select therapeutic categories..."
                  maxTagCount="responsive"
                  disabled={isClassMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.therapeutic_categories || [], 'therapeutic_categories')}
                </Select>
                {isClassMode && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because Drug Class filter is active. Clear Drug Class to filter by Therapeutic Category.
                  </Text>
                )}
              </div>
            </>
          )}

          {/* Glaucoma-specific filters */}
          {(filters.therapeuticArea === 'Glaucoma' || filters.therapeuticArea === 'Glaucoma - Focus') && (
            <>
              {/* Drug Class Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Drug Class
                </Text>
                <Select
                  mode="multiple"
                  value={filters.drugClasses}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('drugClasses', value)}
                  placeholder="Select drug classes..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isDistributionMode || isDrugTypeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.drug_classes || [], 'drug_classes')}
                </Select>
                {(isCategoryMode || isDistributionMode || isDrugTypeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Drug Class.
                  </Text>
                )}
              </div>

              {/* Indication/Therapeutic Category Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Indication
                </Text>
                <Select
                  mode="multiple"
                  value={filters.therapeuticCategories}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('therapeuticCategories', value)}
                  placeholder="Select indications..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isDistributionMode || isDrugTypeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.therapeutic_categories || [], 'therapeutic_categories')}
                </Select>
                {(isClassMode || isDistributionMode || isDrugTypeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Indication.
                  </Text>
                )}
              </div>

              {/* Distribution Channel Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Distribution Channel
                </Text>
                <Select
                  mode="multiple"
                  value={filters.distributionChannels}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('distributionChannels', value)}
                  placeholder="Select distribution channels..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isCategoryMode || isDrugTypeMode}
                >
                  {filterOptions?.distribution_channels?.map((channel: string) => (
                    <Option key={channel} value={channel}>{channel}</Option>
                  )) || []}
                </Select>
                {(isClassMode || isCategoryMode || isDrugTypeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Distribution Channel.
                  </Text>
                )}
              </div>

              {/* Drug Type Filter (Originator vs Generic) */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Drug Type
                </Text>
                <Select
                  mode="multiple"
                  value={filters.drugTypes}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('drugTypes', value)}
                  placeholder="Select drug types..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isCategoryMode || isDistributionMode}
                >
                  {filterOptions?.drug_types?.map((drugType: string) => (
                    <Option key={drugType} value={drugType}>{drugType}</Option>
                  )) || []}
                </Select>
                {(isClassMode || isCategoryMode || isDistributionMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Drug Type.
                  </Text>
                )}
              </div>
            </>
          )}

          {/* Retinal Disorders specific filters */}
          {(filters.therapeuticArea === 'Retinal Disorders' || filters.therapeuticArea === 'Retinal - Focus') && (
            <>
              {/* Indication Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Indication
                </Text>
                <Select
                  mode="multiple"
                  value={filters.therapeuticCategories}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('therapeuticCategories', value)}
                  placeholder="Select indications..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isRouteMode || isDistributionMode || isDrugTypeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.therapeutic_categories || [], 'therapeutic_categories')}
                </Select>
                {(isClassMode || isRouteMode || isDistributionMode || isDrugTypeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Indication.
                  </Text>
                )}
              </div>

              {/* Drug Class Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Drug Class
                </Text>
                <Select
                  mode="multiple"
                  value={filters.drugClasses}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={handleDrugClassChange}
                  placeholder="Select drug classes..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.drug_classes || [], 'drug_classes')}
                </Select>
                {(isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Drug Class.
                  </Text>
                )}
              </div>

              {/* Route of Administration Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Route of Administration
                </Text>
                <Select
                  mode="multiple"
                  value={filters.routesOfAdministration}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('routesOfAdministration', value)}
                  placeholder="Select routes..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isCategoryMode || isDistributionMode || isDrugTypeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.routes_of_administration || [], 'routes_of_administration')}
                </Select>
                {(isClassMode || isCategoryMode || isDistributionMode || isDrugTypeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Route of Administration.
                  </Text>
                )}
              </div>

              {/* Distribution Channel Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Distribution Channel
                </Text>
                <Select
                  mode="multiple"
                  value={filters.distributionChannels}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('distributionChannels', value)}
                  placeholder="Select distribution channels..."
                  maxTagCount="responsive"
                  disabled={isClassMode || isCategoryMode || isRouteMode || isDrugTypeMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.distribution_channels || [], 'distribution_channels')}
                </Select>
                {(isClassMode || isCategoryMode || isRouteMode || isDrugTypeMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Distribution Channel.
                  </Text>
                )}
              </div>
            </>
          )}

          {/* Ophthalmology - In Sight specific filters */}
          {(filters.therapeuticArea === 'Ophthalmology' || filters.therapeuticArea === 'Ophthalmology - In Sight') && (
            <>
              {/* Indication Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Indication
                </Text>
                <Select
                  mode="multiple"
                  value={filters.therapeuticCategories}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('therapeuticCategories', value)}
                  placeholder="Select indications..."
                  maxTagCount="responsive"
                  disabled={isRouteMode || isDistributionMode || isDrugTypeMode || isPrescriptionStatusMode || isPatentStatusMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.therapeutic_categories || [], 'therapeutic_categories')}
                </Select>
                {(isRouteMode || isDistributionMode || isDrugTypeMode || isPrescriptionStatusMode || isPatentStatusMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Indication.
                  </Text>
                )}
              </div>

              {/* Route of Administration Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Route of Administration
                </Text>
                <Select
                  mode="multiple"
                  value={filters.routesOfAdministration}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('routesOfAdministration', value)}
                  placeholder="Select routes of administration..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isDistributionMode || isDrugTypeMode || isPrescriptionStatusMode || isPatentStatusMode}
                >
                  {renderOptionsWithHierarchy(filterOptions?.routes_of_administration || [], 'routes_of_administration')}
                </Select>
                {(isCategoryMode || isDistributionMode || isDrugTypeMode || isPrescriptionStatusMode || isPatentStatusMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Route of Administration.
                  </Text>
                )}
              </div>

              {/* Distribution Channel Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Distribution Channel
                </Text>
                <Select
                  mode="multiple"
                  value={filters.distributionChannels}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('distributionChannels', value)}
                  placeholder="Select distribution channels..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isRouteMode || isDrugTypeMode || isPrescriptionStatusMode || isPatentStatusMode}
                >
                  {filterOptions?.distribution_channels?.map((channel: string) => (
                    <Option key={channel} value={channel}>{channel}</Option>
                  )) || []}
                </Select>
                {(isCategoryMode || isRouteMode || isDrugTypeMode || isPrescriptionStatusMode || isPatentStatusMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Distribution Channel.
                  </Text>
                )}
              </div>

              {/* Drug Type Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Drug Type
                </Text>
                <Select
                  mode="multiple"
                  value={filters.drugTypes}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('drugTypes', value)}
                  placeholder="Select drug types..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isRouteMode || isDistributionMode || isPrescriptionStatusMode || isPatentStatusMode}
                >
                  {filterOptions?.drug_types?.map((drugType: string) => (
                    <Option key={drugType} value={drugType}>{drugType}</Option>
                  )) || []}
                </Select>
                {(isCategoryMode || isRouteMode || isDistributionMode || isPrescriptionStatusMode || isPatentStatusMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Drug Type.
                  </Text>
                )}
              </div>

              {/* Prescription Status Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Prescription Status
                </Text>
                <Select
                  mode="multiple"
                  value={filters.prescriptionStatuses}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('prescriptionStatuses', value)}
                  placeholder="Select prescription statuses..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isPatentStatusMode}
                >
                  {filterOptions?.prescription_statuses?.map((status: string) => (
                    <Option key={status} value={status}>{status}</Option>
                  )) || []}
                </Select>
                {(isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isPatentStatusMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Prescription Status.
                  </Text>
                )}
              </div>

              {/* Patent Status Filter */}
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Patent Status
                </Text>
                <Select
                  mode="multiple"
                  value={filters.patentStatuses}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('patentStatuses', value)}
                  placeholder="Select patent statuses..."
                  maxTagCount="responsive"
                  disabled={isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isPrescriptionStatusMode}
                >
                  {filterOptions?.patent_statuses?.map((status: string) => (
                    <Option key={status} value={status}>{status}</Option>
                  )) || []}
                </Select>
                {(isCategoryMode || isRouteMode || isDistributionMode || isDrugTypeMode || isPrescriptionStatusMode) && (
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Disabled because another filter is active. Clear other filters to filter by Patent Status.
                  </Text>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Regional Analysis Tab - Shows Region + Year Range (no Therapeutic Area) */}
      {isRegionalAnalysisTab && (
        <>
          {/* Year Range Slider */}
          <div style={{ marginBottom: '24px', width: '100%' }}>
            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
              Year Range
            </Text>
            <div style={{ width: '100%', paddingLeft: '7px', paddingRight: '7px', boxSizing: 'border-box' }}>
              <Slider
                range
                min={dynamicYearRange.min_year}
                max={dynamicYearRange.max_year}
                value={Array.isArray(filters.yearRange) && filters.yearRange.length === 2 
                  ? filters.yearRange 
                  : [dynamicYearRange.min_year, dynamicYearRange.max_year]}
                onChange={handleYearRangeChange}
                marks={{
                  [dynamicYearRange.min_year]: dynamicYearRange.min_year.toString(),
                  [dynamicYearRange.max_year]: dynamicYearRange.max_year.toString()
                }}
                tooltip={{
                  formatter: (value) => value?.toString()
                }}
                style={{ width: '100%', margin: '0' }}
              />
            </div>
          </div>

          {/* Region Filter */}
          <div style={{ marginBottom: '16px' }}>
            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
              Region
            </Text>
            <Select
              mode="multiple"
              value={filters.region}
              style={{ width: '100%' }}
              size="small"
              onChange={(value) => updateFilter('region', value)}
              placeholder="Select regions..."
              maxTagCount="responsive"
            >
              {filterOptions.regions.map((region: string) => (
                <Option key={region} value={region}>{region}</Option>
              ))}
            </Select>
          </div>
        </>
      )}

      {/* Country Insights Tab - Shows Country and Date filters */}
      {isCountryInsightsTab && (
        <>
          {/* Year Range Slider */}
          <div style={{ marginBottom: '24px', width: '100%' }}>
            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
              Year Range
            </Text>
            <div style={{ width: '100%', paddingLeft: '7px', paddingRight: '7px', boxSizing: 'border-box' }}>
              <Slider
                range
                min={dynamicYearRange.min_year}
                max={dynamicYearRange.max_year}
                value={Array.isArray(filters.yearRange) && filters.yearRange.length === 2 
                  ? filters.yearRange 
                  : [dynamicYearRange.min_year, dynamicYearRange.max_year]}
                onChange={handleYearRangeChange}
                marks={{
                  [dynamicYearRange.min_year]: dynamicYearRange.min_year.toString(),
                  [dynamicYearRange.max_year]: dynamicYearRange.max_year.toString()
                }}
                tooltip={{
                  formatter: (value) => value?.toString()
                }}
                style={{ width: '100%', margin: '0' }}
              />
            </div>
          </div>

          {/* Country Filter */}
          <div style={{ marginBottom: '16px' }}>
            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
              Countries
            </Text>
            <Select
              mode="multiple"
              value={filters.countries}
              style={{ width: '100%' }}
              size="small"
              onChange={(value) => updateFilter('countries', value)}
              placeholder="Select countries..."
              maxTagCount="responsive"
              allowClear
            >
              {filterOptions.countries.map((country: string) => (
                <Option key={country} value={country}>{country}</Option>
              ))}
            </Select>
          </div>
        </>
      )}

            {/* Other tabs (Drug Pipeline, Company Performance) */}
      {!isMarketOverviewTab && !isMarketBreakdownsTab && !isRegionalAnalysisTab && !isCountryInsightsTab && !isExecutiveSummaryTab && (
        <>
          {/* Show different filters based on tab */}
           {activeTab === 'drug-pipeline' && (
             <>
               <div style={{ marginBottom: '16px' }}>
                 <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                   Pipeline Status
                 </Text>
                <Select
                  value={filters.pipelineStatus}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(value) => updateFilter('pipelineStatus', value)}
                >
                  <Option value="active">Active</Option>
                  <Option value="discontinued">Discontinued</Option>
                  {filters.therapeuticArea !== 'Skin Cancer - Trial Version' && 
                   filters.therapeuticArea !== 'Skin Cancer - Focus' &&
                   filters.therapeuticArea !== 'Skin Cancer' &&
                   filters.therapeuticArea !== 'Psoriasis - Focus' &&
                   filters.therapeuticArea !== 'Psoriasis' &&
                   filters.therapeuticArea !== 'Cell, Gene & RNA Therapies' &&
                   filters.therapeuticArea !== 'Cell, Gene & RNA Therapies - In Sight' && (
                    <Option value="marketed">Marketed</Option>
                  )}
                  {/* Cell, Gene & RNA-specific statuses: Approved + Suspended (in addition to Active/Discontinued) */}
                  {(filters.therapeuticArea === 'Cell, Gene & RNA Therapies' ||
                    filters.therapeuticArea === 'Cell, Gene & RNA Therapies - In Sight') && (
                    <>
                      <Option value="approved">Approved</Option>
                      <Option value="suspended">Suspended</Option>
                    </>
                  )}
                </Select>
               </div>

               <div style={{ marginBottom: '16px' }}>
                 <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                   Company (Developer)
                 </Text>
                 <Select
                   mode="multiple"
                   value={filters.companySearchMulti}
                   style={{ width: '100%' }}
                   size="small"
                   onChange={(value) => updateFilter('companySearchMulti', value)}
                   placeholder="Select company..."
                   maxTagCount="responsive"
                   allowClear
                   loading={companyLoading}
                   showSearch
                   filterOption={(input, option) => {
                     const value = option?.value;
                     if (value && typeof value === 'string') {
                       return value.toLowerCase().includes(input.toLowerCase());
                     }
                     return false;
                   }}
                   notFoundContent={companyLoading ? <Spin size="small" /> : 'No companies found'}
                   dropdownStyle={{ maxHeight: 300 }}
                   placement="bottomLeft"
                 >
                    {companyOptions.map((company: string) => (
                     <Option key={company} value={company}>{company}</Option>
                   ))}
                 </Select>
                 {filters.companySearchMulti.length > 0 && (
                   <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                     {filters.companySearchMulti.length} {filters.companySearchMulti.length === 1 ? 'company' : 'companies'} selected
                   </Text>
                 )}
               </div>

               <div style={{ marginBottom: '16px' }}>
                 <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                   Phase
                 </Text>
                 <Select
                   mode="multiple"
                   value={filters.phaseFilter}
                   style={{ width: '100%' }}
                   size="small"
                   onChange={(value) => updateFilter('phaseFilter', value)}
                   allowClear
                   maxTagCount="responsive"
                 >
                    {(isDrugPipelineTab ? pipelineFilterOptions.phases : filterOptions.phases).map((phase: string) => (
                     <Option key={phase} value={phase}>{phase}</Option>
                   ))}
                 </Select>
               </div>

               {/* Hide Drug Classification for Psoriasis, as it's not applicable */}
               {filters.therapeuticArea !== 'Psoriasis' && (
                 <div style={{ marginBottom: '16px' }}>
                   <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                     Drug Classification
                   </Text>
                   <Select
                     mode="multiple"
                     value={filters.classificationFilter}
                     style={{ width: '100%' }}
                     size="small"
                     onChange={(value) => updateFilter('classificationFilter', value)}
                     allowClear
                     maxTagCount="responsive"
                   >
                      {(isDrugPipelineTab ? pipelineFilterOptions.classifications : filterOptions.classifications).map((cls: string) => (
                       <Option key={cls} value={cls}>{cls}</Option>
                     ))}
                   </Select>
                 </div>
               )}

              {/* Mechanisms Filter (hide for Dermatology, Skin Cancer, Trial Version, Ophthalmology areas, and CGT/RNA in Drug Pipeline) */}
              {isDrugPipelineTab && filters.therapeuticArea !== 'Dermatology' && filters.therapeuticArea !== 'Skin Cancer - Focus' && filters.therapeuticArea !== 'Skin Cancer' && filters.therapeuticArea !== 'Skin Cancer - Trial Version' && filters.therapeuticArea !== 'Ophthalmology' && filters.therapeuticArea !== 'Ophthalmology - In Sight' && filters.therapeuticArea !== 'Retinal Disorders' && filters.therapeuticArea !== 'Retinal - Focus' && filters.therapeuticArea !== 'Glaucoma' && filters.therapeuticArea !== 'Glaucoma - Focus' && filters.therapeuticArea !== 'Cell, Gene & RNA Therapies' && filters.therapeuticArea !== 'Cell, Gene & RNA Therapies - In Sight' && (
                 <div style={{ marginBottom: '16px' }}>
                   <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                     Mechanisms
                   </Text>
                   <Select
                     mode="multiple"
                     value={filters.mechanisms}
                     style={{ width: '100%' }}
                     size="small"
                     onChange={(value) => updateFilter('mechanisms', value)}
                     placeholder="Select mechanisms..."
                     maxTagCount="responsive"
                   >
                      {pipelineFilterOptions.mechanisms?.map((mechanism: string) => (
                       <Option key={mechanism} value={mechanism}>{mechanism}</Option>
                     )) || []}
                   </Select>
                 </div>
               )}

               {/* Debug: Show current therapeutic area and tab */}
               {console.log('Current therapeutic area:', filters.therapeuticArea)}
               {console.log('Is Drug Pipeline Tab:', isDrugPipelineTab)}

               {/* Indication Filter for Skin Cancer in Drug Pipeline */}
               {isDrugPipelineTab && (filters.therapeuticArea === 'Skin Cancer - Focus' || filters.therapeuticArea === 'Skin Cancer' || filters.therapeuticArea === 'Skin Cancer - Trial Version') && (
                 <div style={{ marginBottom: '16px' }}>
                   <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                     Indication
                   </Text>
                   <Select
                     mode="multiple"
                     value={filters.indications}
                     style={{ width: '100%' }}
                     size="small"
                     onChange={(value) => updateFilter('indications', value)}
                     placeholder="Select indications..."
                     maxTagCount="responsive"
                   >
                      {pipelineFilterOptions.indications?.map((indication: string) => (
                       <Option key={indication} value={indication}>{indication}</Option>
                     )) || []}
                   </Select>
                 </div>
               )}

               <div style={{ marginBottom: '16px' }}>
                 <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                   Drug Name
                 </Text>
                 <Input
                   size="small"
                   placeholder="Search drug name"
                   value={filters.drugNameSearch}
                   onChange={(e) => updateFilter('drugNameSearch', e.target.value)}
                   allowClear
                 />
               </div>

               {/* Psoriasis-specific filters */}
               {filters.therapeuticArea === 'Psoriasis' && (
                 <>
                   <div style={{ marginBottom: '16px' }}>
                     <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                       Indications
                     </Text>
                     <Select
                       mode="multiple"
                       value={filters.indications}
                       style={{ width: '100%' }}
                       size="small"
                       onChange={(value) => updateFilter('indications', value)}
                       placeholder="Select indications..."
                       maxTagCount="responsive"
                     >
                        {pipelineFilterOptions.indications?.map((indication: string) => (
                         <Option key={indication} value={indication}>{indication}</Option>
                       )) || []}
                     </Select>
                   </div>

                   <div style={{ marginBottom: '16px' }}>
                     <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                       Routes
                     </Text>
                     <Select
                       mode="multiple"
                       value={filters.routes}
                       style={{ width: '100%' }}
                       size="small"
                       onChange={(value) => updateFilter('routes', value)}
                       placeholder="Select routes..."
                       maxTagCount="responsive"
                     >
                        {pipelineFilterOptions.routes?.map((route: string) => (
                         <Option key={route} value={route}>{route}</Option>
                       )) || []}
                     </Select>
                   </div>
                 </>
               )}
               
               {/* Dermatology-specific filters */}
               {filters.therapeuticArea === 'Dermatology' && (
                 <>
                   <div style={{ marginBottom: '16px' }}>
                     <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                       Drug Type
                     </Text>
                     <Select
                       mode="multiple"
                       value={filters.drugTypes}
                       style={{ width: '100%' }}
                       size="small"
                       onChange={(value) => updateFilter('drugTypes', value)}
                       placeholder="Select drug types..."
                       maxTagCount="responsive"
                     >
                        {pipelineFilterOptions.drug_types?.map((drugType: string) => (
                         <Option key={drugType} value={drugType}>{drugType}</Option>
                       )) || []}
                     </Select>
                   </div>
                   <div style={{ marginBottom: '16px' }}>
                     <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                       Route
                     </Text>
                     <Select
                       mode="multiple"
                       value={filters.routes}
                       style={{ width: '100%' }}
                       size="small"
                       onChange={(value) => updateFilter('routes', value)}
                       placeholder="Select routes..."
                       maxTagCount="responsive"
                     >
                        {pipelineFilterOptions.routes?.map((route: string) => (
                         <Option key={route} value={route}>{route}</Option>
                       )) || []}
                     </Select>
                   </div>
                   <div style={{ marginBottom: '16px' }}>
                     <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                       Indication
                     </Text>
                     <Select
                       mode="multiple"
                       value={filters.indications}
                       style={{ width: '100%' }}
                       size="small"
                       onChange={(value) => updateFilter('indications', value)}
                       placeholder="Select indications..."
                       maxTagCount="responsive"
                     >
                        {pipelineFilterOptions.indications?.map((indication: string) => (
                         <Option key={indication} value={indication}>{indication}</Option>
                       )) || []}
                     </Select>
                   </div>
                 </>
               )}

               {/* Ophthalmology-specific filters */}
               {(filters.therapeuticArea === 'Ophthalmology' || filters.therapeuticArea === 'Retinal Disorders' || filters.therapeuticArea === 'Retinal - Focus') && (
                 <>
                   <div style={{ marginBottom: '16px' }}>
                     <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                       Indication
                     </Text>
                     <Select
                       mode="multiple"
                       value={filters.indications}
                       style={{ width: '100%' }}
                       size="small"
                       onChange={(value) => updateFilter('indications', value)}
                       placeholder="Select indications..."
                       maxTagCount="responsive"
                     >
                        {pipelineFilterOptions.indications?.map((indication: string) => (
                         <Option key={indication} value={indication}>{indication}</Option>
                       )) || []}
                     </Select>
                   </div>

                   <div style={{ marginBottom: '16px' }}>
                     <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                       Mechanism of Action
                     </Text>
                     <Select
                       mode="multiple"
                       value={filters.mechanisms}
                       style={{ width: '100%' }}
                       size="small"
                       onChange={(value) => updateFilter('mechanisms', value)}
                       placeholder="Select mechanisms..."
                       maxTagCount="responsive"
                     >
                        {pipelineFilterOptions.mechanisms?.map((mechanism: string) => (
                         <Option key={mechanism} value={mechanism}>{mechanism}</Option>
                       )) || []}
                     </Select>
                   </div>
                 </>
               )}

              {/* Glaucoma-specific filters (without Indication) */}
              {(filters.therapeuticArea === 'Glaucoma - Focus' || filters.therapeuticArea === 'Glaucoma') && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                      Mechanism of Action
                    </Text>
                    <Select
                      mode="multiple"
                      value={filters.mechanisms}
                      style={{ width: '100%' }}
                      size="small"
                      onChange={(value) => updateFilter('mechanisms', value)}
                      placeholder="Select mechanisms..."
                      maxTagCount="responsive"
                    >
                       {pipelineFilterOptions.mechanisms?.map((mechanism: string) => (
                        <Option key={mechanism} value={mechanism}>{mechanism}</Option>
                      )) || []}
                    </Select>
                  </div>
                </>
              )}

              {/* Cell, Gene & RNA-specific filters (Therapy Class, Therapeutic Approach, Modality, Indication) */}
              {(filters.therapeuticArea === 'Cell, Gene & RNA Therapies' || filters.therapeuticArea === 'Cell, Gene & RNA Therapies - In Sight') && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                      Therapy Class
                    </Text>
                    <Select
                      mode="multiple"
                      value={filters.classificationFilter}
                      style={{ width: '100%' }}
                      size="small"
                      onChange={(value) => updateFilter('classificationFilter', value)}
                      placeholder="Select therapy classes..."
                      maxTagCount="responsive"
                    >
                      {pipelineFilterOptions.therapy_classes?.map((tc: string) => (
                        <Option key={tc} value={tc}>{tc}</Option>
                      )) || []}
                    </Select>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                      Therapeutic Approach
                    </Text>
                    <Select
                      mode="multiple"
                      value={filters.mechanisms}
                      style={{ width: '100%' }}
                      size="small"
                      onChange={(value) => updateFilter('mechanisms', value)}
                      placeholder="Select therapeutic approach..."
                      maxTagCount="responsive"
                    >
                      {pipelineFilterOptions.therapeutic_approaches?.map((ta: string) => (
                        <Option key={ta} value={ta}>{ta}</Option>
                      )) || []}
                    </Select>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                      Modality
                    </Text>
                    <Select
                      mode="multiple"
                      value={filters.drugTypes}
                      style={{ width: '100%' }}
                      size="small"
                      onChange={(value) => updateFilter('drugTypes', value)}
                      placeholder="Select modality..."
                      maxTagCount="responsive"
                    >
                      {pipelineFilterOptions.modalities?.map((m: string) => (
                        <Option key={m} value={m}>{m}</Option>
                      )) || []}
                    </Select>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                      Indication
                    </Text>
                    <Select
                      mode="multiple"
                      value={filters.indications}
                      style={{ width: '100%' }}
                      size="small"
                      onChange={(value) => updateFilter('indications', value)}
                      placeholder="Select indications..."
                      maxTagCount="responsive"
                    >
                      {pipelineFilterOptions.indications?.map((indication: string) => (
                        <Option key={indication} value={indication}>{indication}</Option>
                      )) || []}
                    </Select>
                  </div>
                </>
              )}
           </>
         )}

          {/* Company Search (only show on Company Performance tab) */}
           {activeTab === 'company-performance' && (
             <>
               {/* Multi-select Company Filter */}
               <div style={{ marginBottom: '16px' }}>
                 <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                   Select Companies
                 </Text>
                 <Select
                   mode="multiple"
                   value={filters.companySearchMulti}
                   style={{ width: '100%' }}
                   size="small"
                   onChange={(value) => updateFilter('companySearchMulti', value)}
                   placeholder="Select company..."
                   maxTagCount="responsive"
                   allowClear
                   loading={companyLoading}
                   showSearch
                   filterOption={(input, option) => {
                     const value = option?.value;
                     if (value && typeof value === 'string') {
                       return value.toLowerCase().includes(input.toLowerCase());
                     }
                     return false;
                   }}
                   notFoundContent={companyLoading ? <Spin size="small" /> : 'No companies found'}
                   dropdownStyle={{ maxHeight: 300 }}
                 >
                    {companyOptions.map((company: string) => (
                     <Option key={company} value={company}>{company}</Option>
                   ))}
                 </Select>
                   {filters.companySearchMulti.length > 0 && (
                     <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                       {filters.companySearchMulti.length} {filters.companySearchMulti.length === 1 ? 'company' : 'companies'} selected
                     </Text>
                   )}
                   <Text type="secondary" style={{ fontSize: '10px', marginTop: '4px', display: 'block', fontStyle: 'italic' }}>
                     Only companies with active drugs are shown
                   </Text>
                 </div>

               </>
             )}
        </>
      )}

      {/* Active Filters summary removed by request to reduce panel height */}
    </div>
  );
};

export default FilterPanel;